import {useState} from "react";
import {
    convertCamelCaseToSpaceDelimited,
    isNullOrUndefined,
    isTrimmedStringEmpty
} from "../../Utilities/commonUtilities";
import {isArrayNullOrEmpty, optionIndexInArray} from "../../Utilities/Types/arrayUtilities";
import {createListFromObject} from "../../Utilities/Types/listUtilities";
import {deepCopyObject, isObjectNullOrEmpty, objectHasProperty} from "../../Utilities/Types/objectUtilities";

import {inputStyles, otherTextSeparator, YesOrNo} from "./inputConstants";
import type {InputChangeEvent} from "../../types";

export interface InputHook<T = string> {
    value: T;
    onChange: (e: InputChangeEvent | T) => void;
    setValue: (value: T) => void;
}

export interface InputFormField {
    value: string;
    onChange: (e: InputChangeEvent, id?: string) => void;
    setValue: (value: string) => void;
    setError: (error: string) => void;
    name: string;
    error: string;
    label: string;
}

export interface ButtonClassesConfig {
    className?: string;
    hasSpacing?: boolean;
    isTertiary?: boolean;
    isExpanded?: boolean;
    isLarge?: boolean;
    isLink?: boolean;
    isPrimary?: boolean;
    isRibbon?: boolean;
    buttonType?: string;
    notCentered?: boolean;
}

export function useInput<T = string>(initialValue: T): InputHook<T> {
const [value, setValue] = useState(initialValue);

function handleChange(e: InputChangeEvent | T) {
    const value = (e && typeof e === 'object' && 'target' in e) ? e.target.value as T : e as T;
    setValue(value);
    }

    return {
        onChange: handleChange,
        setValue,
        value,
    };
}

export type InputFormResult<T> = { [K in keyof T]: InputFormField } & { setValue: (value: Record<string, unknown>) => void } & Record<string, InputFormField>;

export function useInputFormNoStateForTesting<T extends Record<string, unknown>>(initialValue: T, callbackAfterChange?: () => void): InputFormResult<T> {
let items = {...initialValue} as Record<string, unknown>;
const setItems = (value: Record<string, unknown>) => items = value;
    return useInputFormAbstract(callbackAfterChange, items, setItems) as InputFormResult<T>;
}

export function useInputForm<T extends Record<string, unknown>>(initialValue: T, callbackAfterChange?: () => void): InputFormResult<T> {
    const [items, setItems] = useState(deepCopyObject(initialValue) as Record<string, unknown>);
    return useInputFormAbstract(callbackAfterChange, items, setItems) as InputFormResult<T>;
}

export function useInputFormAbstract(callbackAfterChange: (() => void) | undefined, items: Record<string, unknown>, setItems: (value: Record<string, unknown>) => void) {

function handleChange(this: InputFormField | undefined, e: InputChangeEvent, id?: string) {
    const name = id ? id : e.target.name;
    const item = this ? this : items[name as string] as InputFormField;
    item.setValue(e.target.value);

    if (callbackAfterChange) callbackAfterChange();
}

function setValue(this: InputFormField, value: string) {
    const result = {...items};
    if (value !== (result[this.name] as InputFormField).value) {
        (result[this.name] as InputFormField).value = value;
        setItems(result);
    }
}

function setError(this: InputFormField, error: string) {
    const result = {...items};
    if (error !== (result[this.name] as InputFormField).error) {
        (result[this.name] as InputFormField).error = error;
        setItems(result);
    }
}

    for (let property in items) {
        if (!objectHasProperty(items, property)) break;
        const item = items[property];
        if (isNullOrUndefined(item) || typeof item !== "object" || Array.isArray(item))
            items[property] = {
                value: item,
                onChange: handleChange,
                setValue,
                setError,
                name: property,
                error: "",
                label: convertCamelCaseToSpaceDelimited(property, true),
            };
    }

    return {
        ...items,
        setValue: setItems
    };
}

export const clearInputFormFields = (object: Record<string, InputFormField> & Record<string, unknown>): void => {

    let convertedObject = convertInputFormToObject(object);

    for (let property in convertedObject) {
        if (objectHasProperty(convertedObject, property) && String(property) !== `setValue` && !isMethod(convertedObject, property) && String(property) !== `recordsPerPage`) {
            object[property].setValue("");
        }
    }
};

function isMethod(obj: Record<string, unknown>, propName: string): boolean {
    return obj[propName] instanceof Function;
}

export const clearInputFormErrorFields = (object: Record<string, InputFormField> & Record<string, unknown>): void => {
    for (let property in object) {
        if (objectHasProperty(object, property) && String(property) !== `setValue`) {
            object[property].setError && object[property].setError("");
        }
    }
};

export const reinitializeInputFormFields = (object: Record<string, InputFormField> & Record<string, unknown>, initialValues: Record<string, unknown>): void => {
    for (let property in object) {
        if (objectHasProperty(object, property) && String(property) !== `setValue`) {
            const newValue = objectHasProperty(initialValues, property) ? String(initialValues[property] ?? '') : "";
            if (!isObjectNullOrEmpty(object[property])) {
                object[property].setValue(newValue);
                object[property].setError("");
            }
        }
    }
};

export const reinitializeSpecificInputFormFields = (object: Record<string, InputFormField> & Record<string, unknown>, fieldsToReinitialize: string[], newValue = ""): void => {
Object.keys(object).forEach((property: string) => {
        if (fieldsToReinitialize.includes(property) && object[property] && typeof object[property].setValue === 'function') {
            object[property].setValue(newValue);
            object[property].setError("");
        }
    });
};


export const inputFormIsValid = (object: Record<string, InputFormField> & Record<string, unknown>): boolean => {
    let isValid = true;

    for (let property in object) {
        if (objectHasProperty(object, property)) {
            if (!isTrimmedStringEmpty(object[property].error) && isValid) {
                isValid = false;
                focusOnFormElement(property);
            }
        }
    }

    return isValid;
};

export function focusOnFormElement(elementId: string): void {
    let element: HTMLElement | null = document.getElementById(elementId);

    if (!element) {
        element = document.getElementById(`${elementId}_0`);
    }

    if (!element) {
        const elements = document.getElementsByClassName(elementId);
        if (!elements || elements.length === 0)
            element = null;
        else
            element = elements[0] as HTMLElement;
    }

    if (!element)
        return;

    element.focus();
    if (element.scrollIntoView)
        element.scrollIntoView(false);
}

export function convertInputFormToObject(form: Record<string, unknown>): Record<string, unknown> {
let object = deepCopyObject(form);
if (!object) return {};
for (let property in object) {
    if (objectHasProperty(object, property)) {
        const item = object[property] as Record<string, unknown>;

        if (objectHasProperty(item, "onChange"))
            object[property] = item.value;
        }
    }
    delete object.setValue;
    return object;
}

export function createFakeEvent(value: string, id: string, object: Record<string, unknown> = {}, targetObject: Record<string, unknown> = {}) {
    return {
        target: {...{value, id, name: id}, ...targetObject},
        ...object,
        ...{
            preventDefault: () => {
            },
        },
    };
}

export function createYesNoObject(isBoolean: boolean = true) {
    return {
        Yes: isBoolean ? "true" : YesOrNo.YES,
        No: isBoolean ? "false" : YesOrNo.NO,
    };
}

export function createIsActiveObject() {
    return {
        All: "",
        Yes: "true",
        No:  "false",
    };
}

export function createYesNoList(isBoolean: boolean = true) {
    const options = createYesNoObject(isBoolean);
    return createListFromObject(options);
}

export function createIsActiveList() {
    const options = createIsActiveObject();
    return createListFromObject(options);
}

export function isValueInArray(array: string[], value: string, wholeWord = true): boolean {
    return optionIndexInArray(array, value, wholeWord) > -1;
}

export function removeValueInArray(array: string[], value: string, wholeWord = true): void {
    const index = optionIndexInArray(array, value, wholeWord);
    array.splice(index, 1);
}

export function updateCheckboxListArray(
    optionChanged: string,
    currentOptions: string[],
    allOptions: string[],
): string[] {
    const isChecked =
        optionIndexInArray(currentOptions, optionChanged, false) > -1;
    let optionsSelected: string[] = [];

    if (isChecked) {
        optionsSelected = currentOptions.reduce((agg: string[], option) => {
            if (!option.startsWith(optionChanged)) agg.push(option);

            return agg;
        }, []);
    } else {
        for (let i = 0; i < allOptions.length; i++) {
            if (allOptions[i] === optionChanged) optionsSelected.push(optionChanged);
            else {
                let index = optionIndexInArray(currentOptions, allOptions[i], false);
                if (index > -1) optionsSelected.push(currentOptions[index]);
            }
        }
    }

    return optionsSelected;
}

export function updateCheckboxOtherValue(
    arrayOfSelected: string[],
    selectedValue: string,
    otherValue: string,
): void {
    const index = optionIndexInArray(arrayOfSelected, selectedValue, false);
    arrayOfSelected[index] = generateOtherValue(
        selectedValue,
        otherValue,
    );

    if (isValueInArray(arrayOfSelected, selectedValue))
        removeValueInArray(arrayOfSelected, selectedValue);
}

export function generateOtherValue(
    selectedValue: string,
    otherValue: string,
    separator = otherTextSeparator,
): string {
    return `${selectedValue}${separator}${otherValue}`;
}

export function appendedOtherValue(value: string, inputText: string, separator = otherTextSeparator): string {
    const otherText = value.split(inputText + separator);
    if (otherText.length === 2) return otherText[1];
    return "";
}

export function appendedCheckboxOtherValue(
    arrayOfSelected: string[],
    checkboxText: string,
    separator = otherTextSeparator,
): string {
    const otherIndex = optionIndexInArray(
        arrayOfSelected,
        checkboxText + separator,
        false,
    );
    if (otherIndex > -1) {
        const otherText = arrayOfSelected[otherIndex].split(checkboxText + separator);
        if (otherText.length === 2) return otherText[1];
    }

    return "";
}

export function appendedCheckboxOtherValueExists(
    checkboxText: string,
    arrayOfSelected: string[],
    separator = otherTextSeparator,
): boolean {
    const otherIndex = optionIndexInArray(arrayOfSelected, checkboxText, false);
    if (otherIndex > -1) {
        const otherText = arrayOfSelected[otherIndex].split(separator);
        if (
            otherText.length === 1 ||
            (otherText.length === 2 && otherText[1].trim() === "")
        )
            return false;
    }

    return true;
}

export function getButtonClasses({
                                     className = "",
    hasSpacing,
                                     isTertiary,
                                     isExpanded,
                                     isLarge,
                                     isLink,
                                     isPrimary,
                                     isRibbon,
                                     buttonType,
                                     notCentered
                                 }: ButtonClassesConfig): string {
    if (isLink) {
        className += `${inputStyles.button.isLink} ${className}`;
        className += hasSpacing ? inputStyles.button.hasSpacing : "";
        return className;
    }

    className += inputStyles.button.theme;
    className += isRibbon ? inputStyles.button.isRibbon : "";
    className += isLarge ? inputStyles.button.isLarge : "";
    className += isPrimary ? inputStyles.button.isPrimary :
        isTertiary ? inputStyles.button.isTertiary : inputStyles.button.isSecondary;
    className += isExpanded ? inputStyles.button.isExpanded : inputStyles.button.isCollapsed;
    className += buttonType || "";
    className += notCentered ? inputStyles.button.notCentered : "";

    return className;
}

export function isValidEmail(email: string): boolean {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}


export function checkAndSetError(fields: InputFormField[], message?: string): void {
    if (isArrayNullOrEmpty(fields)) return;
    fields.forEach((field: InputFormField) => {
        if (isObjectNullOrEmpty(field)) return;
        const fieldLabel = field.label?.toString() || "Field";
        const fieldValue = field.value?.toString() || "";
        const isFieldEmpty = isTrimmedStringEmpty(fieldValue);
        if (isFieldEmpty && !message) {
            field.setError(`${fieldLabel} is required.`);
        } else if (isFieldEmpty && message) {
            field.setError(message);
        } else if (field.name === "email" && !isValidEmail(fieldValue)) {
            field.setError("Email is not valid.");
        }
    });
}
